import { DataSource, SelectionChange, SelectionModel } from '@angular/cdk/collections';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { TableColumnFilter, TableFilter } from '@dice/app/types/table.types';
import { get as _get } from 'lodash';
import {
  BehaviorSubject,
  combineLatest,
  interval,
  merge,
  Observable,
  of,
  Subject,
  Subscriber,
  Subscription,
} from 'rxjs';
import { debounceTime, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';

// @ts-ignore
export class TableDataSource<T> extends DataSource<T> {
  /**
   * A stream of the filtered arrays of data
   */
  private readonly _filteredData = new BehaviorSubject<T[]>([]);

  private readonly _sortedData = new BehaviorSubject<T[]>([]);

  private readonly selection = new SelectionModel<T>(true, []);

  private _visibleData: BehaviorSubject<T[]> = new BehaviorSubject([]);

  private _visibleDataSubscription: Subscription = Subscription.EMPTY;

  private _data: BehaviorSubject<T[]> = new BehaviorSubject<T[]>([]);

  private _incrementalDataLoadSubscription: Subscription = Subscription.EMPTY;

  private _sort: MatSort;

  private _paginator: MatPaginator;

  private _internalPageChanges: Subject<void> = new Subject();

  private _tableFilter: BehaviorSubject<TableFilter> = new BehaviorSubject(null);

  private worker: Worker;

  set filterPredicate(filterPredicateFn: (data: T, filter: string) => boolean) {
    this.searchFilterPredicate = filterPredicateFn;
  }

  get tableFilter(): TableFilter {
    return this._tableFilter.value;
  }

  set tableFilter(tableFilter: TableFilter) {
    this._tableFilter.next(tableFilter);
  }

  set tableColumnFilter(tableColumnFilter: TableColumnFilter[]) {
    this._tableFilter.next({
      ...this.tableFilter,
      filters: tableColumnFilter,
    });
  }

  /**
   * Filter term that should be used to filter out objects from the data array. To override how
   * data objects match to this filter string, provide a custom function for filterPredicate.
   */
  set filter(filter: string) {
    this._tableFilter.next({
      ...this.tableFilter,
      search: filter,
    });
  }

  get filteredData(): T[] {
    return this._filteredData.getValue();
  }

  get sortedData(): T[] {
    return this._sortedData.getValue();
  }

  get sort(): MatSort {
    return this._sort;
  }

  set sort(matSort: MatSort) {
    this._sort = matSort;
    this.updateChangeSubscriptions();
  }

  get paginator(): MatPaginator {
    return this._paginator;
  }

  set paginator(paginator: MatPaginator) {
    this._paginator = paginator;
    this.updateChangeSubscriptions();
  }

  /**
   * The full array of data
   */
  get data(): T[] {
    return this._data.getValue();
  }

  set data(data: T[]) {
    if (this._data) {
      this._data.next(data ?? []);
    }
  }

  /**
   * The array of currently selected items.
   */
  get selected(): T[] {
    return this.selection.selected;
  }

  /**
   * Whether all visible items are selected.
   */
  get selectedAll(): boolean {
    return this.selection.hasValue() && this.selected.length === this._filteredData.getValue().length;
  }

  /**
   * A stream of changes made on selections.
   */
  get selectionChanged(): Observable<SelectionChange<T>> {
    return this.selection.changed;
  }

  constructor(initialData: T[] = []) {
    super();
    this.data = initialData;
    this.initWorker();
    this.updateChangeSubscriptions();
  }

  private initWorker(): void {
    if (!this.worker) {
      this.worker = new Worker(new URL('./table-sort.worker', import.meta.url), {
        type: 'module',
        name: 'TableSortWorker',
      });
    }
  }

  private updateChangeSubscriptions(): void {
    // Watch for base data or filter changes to provide a filtered set of data.
    const dataStream = this._data;

    const filteredData = combineLatest([dataStream, this._tableFilter]).pipe(
      switchMap(([data, filter]: [T[], TableFilter]) => {
        return this._filterDataObservable(data.slice(), filter);
      }),
    );

    // Watch for filtered data or sort changes to provide an ordered set of data.
    const sortChange = this._sort
      ? merge(this._sort.sortChange, this._sort.initialized).pipe(debounceTime(100))
      : of(null);

    const orderedData = combineLatest([filteredData, sortChange]).pipe(
      switchMap(([data]) => {
        return this._orderDataObservable(data);
      }),
    );

    // Watch for ordered data or page changes to provide a paged set of data.
    const pageChange = this._paginator
      ? merge(this._paginator.page, this._internalPageChanges, this._paginator.initialized)
      : of(null);

    const paginatedData = combineLatest([orderedData, pageChange]).pipe(
      map(([data]) => {
        return this._pageData(data);
      }),
    );

    // Watched for paged data changes and send the result to the table to render.
    this._visibleDataSubscription.unsubscribe();

    this._visibleDataSubscription = paginatedData.subscribe((data: T[]) => {
      if (this._visibleData.getValue().length > 0) {
        this._incrementalDataLoadSubscription.unsubscribe();
        this._visibleData.next(data);
      } else {
        this.incrementallyLoadData(data);
      }
    });
  }

  /**
   * Returns a filtered data array where each filter object contains the filter string within
   * the result of the filterTermAccessor function. If no filter is set, returns the data array
   * as provided.
   * @param unfilteredData
   * @param tableFilter
   * @return Observable<T[]>
   */
  private _filterDataObservable(unfilteredData: T[], tableFilter: TableFilter): Observable<T[]> {
    const filteredData =
      !tableFilter || (!tableFilter.filters && !tableFilter.search)
        ? unfilteredData
        : unfilteredData.filter((obj: T) => {
            return this.filterPredicateFn(obj, tableFilter);
          });
    return of(filteredData).pipe(
      tap((data: T[]) => {
        if (this.paginator) {
          this._updatePaginator(data.length);
        }
        this._filteredData.next(data);
      }),
    );
  }

  /**
   * Returns a sorted copy of the data if MatSort has a sort applied, otherwise just returns the
   * data array as provided. Uses the default data accessor for data lookup, unless a
   * sortDataAccessor function is defined.
   * @param unsortedData
   * @return Observable<T[]>
   */
  _orderDataObservable(unsortedData: T[]): Observable<T[]> {
    if (!unsortedData || unsortedData.length === 0 || !this.sort || !this.sort.direction || !this.sort.active) {
      const data = unsortedData || [];
      this._sortedData.next(data);
      return of(data);
    }

    this.worker.postMessage({
      unsortedData,
      properties: {
        direction: this.sort.direction,
        active: this.sort.active,
      },
    });
    return new Observable<T[]>((observer: Subscriber<T[]>) => {
      this.worker.onmessage = ({ data: sortedData }): void => {
        this._sortedData.next(sortedData);
        observer.next(sortedData);
      };
    });
  }

  /**
   * Returns a paged slice of the provided data array according to the provided MatPaginator's page
   * index and length. If there is no paginator provided, returns the data array as provided.
   * @param data
   * @return T[]
   */
  _pageData(data: T[]): T[] {
    if (!this.paginator) {
      return data;
    }
    const startIndex = this.paginator.pageIndex * this.paginator.pageSize;
    return data.slice(startIndex, startIndex + this.paginator.pageSize);
  }

  /**
   * Updates the paginator to reflect the length of the filtered data, and makes sure that the page
   * index does not exceed the paginator's last page. Values are changed in a resolved promise to
   * guard against making property changes within a round of change detection.
   * @param filteredDataLength
   */
  _updatePaginator(filteredDataLength: number): void {
    Promise.resolve().then(() => {
      const { paginator } = this;
      if (!paginator) {
        return;
      }
      paginator.length = filteredDataLength;
      // If the page index is set beyond the page, reduce it to the last page.
      if (paginator.pageIndex > 0) {
        const lastPageIndex = Math.ceil(paginator.length / paginator.pageSize) - 1 || 0;
        const newPageIndex = Math.min(paginator.pageIndex, lastPageIndex);
        if (newPageIndex !== paginator.pageIndex) {
          paginator.pageIndex = newPageIndex;
          // Since the paginator only emits after user-generated changes,
          // we need our own stream, so we know to should re-render the data.
          this._internalPageChanges.next();
        }
      }
    });
  }

  private incrementallyLoadData(data: T[]): void {
    const MILLI_SECONDS = 100;
    const MAX_AMOUNT = data.length;
    const LOAD_AMOUNT = 15;
    let start = 0;
    let end = LOAD_AMOUNT;
    const unsubscriber: Subject<void> = new Subject();

    this._incrementalDataLoadSubscription.unsubscribe();
    this._visibleData.next([]);
    this._incrementalDataLoadSubscription = interval(MILLI_SECONDS)
      .pipe(
        map(() => data.slice(start, end)),
        takeUntil(unsubscriber),
        shareReplay(),
      )
      .subscribe((next: T[]) => {
        start = end;
        if (next.length === 0 || end === MAX_AMOUNT) {
          end = -1;
        } else {
          end = end + LOAD_AMOUNT < MAX_AMOUNT ? end + LOAD_AMOUNT : MAX_AMOUNT;
        }
        this._visibleData.next([...this._visibleData.getValue(), ...next]);
        if (end === -1) {
          unsubscriber.next();
        }
      });
  }

  private filterPredicateFn(data: T, tableFilter: TableFilter): boolean {
    const { search, filters } = tableFilter;
    return this.searchFilterPredicate(data, search) && this.tableColumnFilterPredicate(data, filters);
  }

  private searchFilterPredicate(data: T, searchFilter: string): boolean {
    if (!searchFilter) {
      return true;
    }
    const dataStr = Object.keys(data)
      .reduce((currentTerm, key) => {
        // Use an obscure Unicode character to delimit the words in the concatenated string.
        // This avoids matches where the values of two columns combined will match the user's query
        // (e.g. `Flute` and `Stop` will match `Test`). The character is intended to be something
        // that has a very low chance of being typed in by somebody in a text field. This one in
        // particular is "White up-pointing triangle with dot" from
        // https://en.wikipedia.org/wiki/List_of_Unicode_characters
        return `${currentTerm + data[key]}◬`;
      }, '')
      .toLowerCase();
    const transformedFilter = searchFilter.trim().toLowerCase();
    return dataStr.indexOf(transformedFilter) !== -1;
  }

  private tableColumnFilterPredicate(data: T, tableColumnFilters: TableColumnFilter[]): boolean {
    return (
      !tableColumnFilters ||
      tableColumnFilters.every(({ keyPath, filter }: TableColumnFilter) => {
        return !filter || _get(data, keyPath).trim().toLowerCase() === filter.toLowerCase();
      })
    );
  }

  /**
   * Clears all of the selected items.
   */
  clearSelection(): void {
    this.selection.clear();
  }

  /**
   * Whether an item is selected.
   */
  isSelected(item: T): boolean {
    return this.selection.isSelected(item);
  }

  /**
   * Selects one or more items.
   */
  select(...items: T[]): void {
    this.selection.select(...items);
  }

  /**
   * Toggles an item between selected and deselected.
   */
  toggle(...items: T[]): void {
    items.forEach((item: T) => {
      this.selection.toggle(item);
    });
  }

  /**
   * Toggles the selection of all visible items.
   *
   * If all are selected, deselects all.
   *
   * If some or none are selected, selects all.
   */
  toggleAll(): void {
    if (this.selectedAll) {
      this.selection.clear();
    } else {
      this.selection.select(...this._filteredData.getValue());
    }
  }

  connect(): BehaviorSubject<T[]> {
    return this._visibleData;
  }

  disconnect(): void {
    this._visibleDataSubscription.unsubscribe();
    this._visibleData.next([]);
    this._visibleData.complete();
    this._incrementalDataLoadSubscription.unsubscribe();
    this.selection.clear();
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this._data.next([]);
    this._data.complete();
    delete this._data;
  }
}
